import { clienteDisplay } from '@/lib/types';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Building2, Calendar, Activity, ArrowRight } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import type { Solicitante, Demanda } from '@/lib/types';

export function Internos() {
  const solQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then(r => r.data) });
  const demQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then(r => r.data) });

  const internos: Solicitante[] = useMemo(
    // 08/07: só Ativos — pseudo-clientes consolidados na migração ficam Inativos e não aparecem
    () => (solQ.data ?? []).filter(s => s.categoria === 'Apex Interno' && s.status === 'Ativo').sort((a, b) => a.nome.localeCompare(b.nome)),
    [solQ.data],
  );

  const demandasPorSolicitante: Record<string, Demanda[]> = useMemo(() => {
    const m: Record<string, Demanda[]> = {};
    for (const d of demQ.data ?? []) {
      if (!m[d.solicitanteId]) m[d.solicitanteId] = [];
      m[d.solicitanteId].push(d);
    }
    return m;
  }, [demQ.data]);

  const totalDemandas = internos.reduce((s, sol) => s + (demandasPorSolicitante[sol.id]?.length ?? 0), 0);
  const totalEntregues = internos.reduce((s, sol) =>
    s + (demandasPorSolicitante[sol.id]?.filter(d => d.status === 'Publicada').length ?? 0), 0);
  const totalAtivas = internos.reduce((s, sol) =>
    s + (demandasPorSolicitante[sol.id]?.filter(d => ['Triagem','Em produção','Em revisão','Aguardando aprovação'].includes(d.status)).length ?? 0), 0);

  return (
    <div>
      <PageHeader title="Crescimento · Apex (UN)" />
      <div className="px-6 py-4 space-y-4">
        <div className="grid grid-cols-4 gap-3">
          <Kpi label="Unidades internas" value={String(internos.length)} icon={<Building2 className="w-4 h-4" />} cor="border-purple/40" />
          <Kpi label="Demandas totais" value={String(totalDemandas)} icon={<Briefcase className="w-4 h-4" />} cor="border-blue/40" />
          <Kpi label="Em produção" value={String(totalAtivas)} icon={<Activity className="w-4 h-4" />} cor="border-orange/40" />
          <Kpi label="Finalizadas" value={String(totalEntregues)} icon={<Calendar className="w-4 h-4" />} cor="border-green/40" />
        </div>

        <p className="text-[11px] text-text-2 italic">
          Esses são clientes internos da Apex — não pagantes. Contabilizam custo operacional, mas não entram no MRR do Comercial.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {internos.map(s => {
            const ds = demandasPorSolicitante[s.id] ?? [];
            const ent = ds.filter(d => d.status === 'Publicada').length;
            const ativas = ds.filter(d => ['Triagem','Em produção','Em revisão','Aguardando aprovação'].includes(d.status)).length;
            return (
              <Link
                key={s.id}
                to={`/demandas?solicitante=${encodeURIComponent(s.id)}`}
                className="card border-2 border-purple/40 block hover:border-blue/40 hover:bg-panel-2/50 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-bold mb-1">{clienteDisplay(s.nome)}</h3>
                  <ArrowRight className="w-3.5 h-3.5 text-muted opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
                </div>
                {s.setor && <p className="text-[10.5px] text-text-2 mb-2">{s.setor}</p>}
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-text-2">{ds.length} demandas · {ent} entregues</span>
                  {ativas > 0 && (
                    <span className="bg-orange/15 text-orange px-2 py-0.5 rounded-full text-[10px] font-bold">{ativas} em produção</span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {internos.length === 0 && !solQ.isLoading && (
          <div className="card text-center py-12 text-muted text-sm">
            Nenhum cliente interno cadastrado.
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, icon, cor }: { label: string; value: string; icon: React.ReactNode; cor: string }) {
  return (
    <div className={`bg-panel border border-border border-2 rounded-lg p-3 ${cor}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted font-bold">
        {icon} {label}
      </div>
      <div className="text-2xl font-extrabold text-text mt-1">{value}</div>
    </div>
  );
}
